import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';

const PW_RULES = [
  { key: 'length',  label: '8+ characters',    test: pw => pw.length >= 8 },
  { key: 'upper',   label: 'Uppercase letter',  test: pw => /[A-Z]/.test(pw) },
  { key: 'lower',   label: 'Lowercase letter',  test: pw => /[a-z]/.test(pw) },
  { key: 'number',  label: 'Number',            test: pw => /[0-9]/.test(pw) },
  { key: 'special', label: 'Special character', test: pw => /[^A-Za-z0-9]/.test(pw) },
];

function PasswordRequirements({ password }) {
  if (!password) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, marginBottom: 4 }}>
      {PW_RULES.map(r => {
        const met = r.test(password);
        return (
          <span key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
            color: met ? '#4ade80' : 'rgba(255,255,255,0.3)', transition: 'color 0.15s' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%',
              background: met ? '#4ade80' : 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
            {r.label}
          </span>
        );
      })}
    </div>
  );
}

// ── ResetPasswordPage ──────────────────────────────────────────────────────────
// Rendered at /reset-password?token=<raw-token>
// Does not require an active session.
export default function ResetPasswordPage() {
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [done,    setDone]    = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (!token) setError('No reset token found. Check your link or request a new one from your administrator.');
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    if (next.length < 8)             { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Z]/.test(next))         { setError('Password must include at least one uppercase letter.'); return; }
    if (!/[a-z]/.test(next))         { setError('Password must include at least one lowercase letter.'); return; }
    if (!/[0-9]/.test(next))         { setError('Password must include at least one number.'); return; }
    if (!/[^A-Za-z0-9]/.test(next))  { setError('Password must include at least one special character.'); return; }
    if (next !== confirm)             { setError('Passwords do not match — please re-enter.'); return; }
    setBusy(true); setError('');
    try {
      const res  = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: next }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) { setDone(true); return; }
      setError(body.error || 'Could not reset password. Please try again.');
    } catch (_) { setError('Could not reach the server. Check your connection.'); }
    setBusy(false);
  }

  return (
    <div className="rp-overlay">
      <div className="rp-card">
        <div className="rp-logo">3<em>AI</em>migos</div>

        {done ? (
          <>
            <h1 className="rp-title">Password updated</h1>
            <p className="rp-sub">Your password has been set. You can now log in.</p>
            <button className="rp-btn" onClick={() => { window.location.href = '/'; }}>
              Go to login
            </button>
          </>
        ) : (
          <>
            <h1 className="rp-title">Set new password</h1>
            <p className="rp-sub">Choose a strong password for your account.</p>

            <form className="rp-form" onSubmit={handleSubmit}>
              <label className="rp-label">New password</label>
              <input
                className="rp-input"
                type="password"
                value={next}
                onChange={e => setNext(e.target.value)}
                autoFocus
                required
                minLength={8}
                disabled={!token || busy}
                placeholder="At least 8 characters"
              />
              <PasswordRequirements password={next} />
              <label className="rp-label">Confirm new password</label>
              <input
                className="rp-input"
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                disabled={!token || busy}
              />
              {error && <div className="rp-error">{error}</div>}
              <button
                className="rp-btn"
                type="submit"
                disabled={busy || !token || !next || !confirm}
              >
                {busy ? 'Saving…' : 'Set password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
